import { Sphere, SphereProperties, AssignedPerspectives } from 'types/global';
import { Schema } from 'types/schema';
import {
  assertPerspectiveAssignmentsValid,
  evaluateAssigneeFilter,
} from './validation/validatePerspectiveAssignments';

export interface PerspectiveAssignment {
  perspectiveSystemName: string; // The system name of the perspective (e.g., "center_responsible").
  assignedSphere: Sphere; // The sphere assigned to this perspective.
}

export type PerspectiveAssignments = PerspectiveAssignment[];

/**
 * Thrown when `unplaced: true` is requested alongside a caller-supplied
 * `center_parent` assignment — a sphere cannot both be marked as needing a
 * home and be given one in the same create call.
 */
export class UnplacedWithParentError extends Error {
  constructor() {
    super('unplaced_with_parent: A sphere cannot both have a Purpose parent and be marked unplaced.');
    this.name = 'UnplacedWithParentError';
  }
}

/**
 * The value each of a schema's properties starts with on a new sphere: its `default_value`, coerced
 * to the property's type. Read-only, hidden and edge-defined properties have none here.
 */
export const extractSchemaDefaults = (schema: Schema): Partial<SphereProperties> => {
  const defaults: Partial<SphereProperties> = {};

  schema.properties.forEach((prop) => {
    // Skip read-only, hidden, and edge-only properties
    if (prop.read_only || prop.hidden || prop.defined_on === 'edge') {
      return;
    }
    // `default_value: null` means the property HAS NO DEFAULT — it must be
    // treated exactly like `undefined`. Guarding only on `!== undefined` let
    // null through to the coercions below, which fabricated garbage on every
    // sphere created: String(null) === "null", Number(null) === 0,
    // Boolean(null) === false, new Date(null) === the epoch. The string case
    // is the visible one — 894 organizations carry the literal 4-character
    // value "null" in org_type / industry / size_range because of this.
    if (prop.default_value !== undefined && prop.default_value !== null) {
      switch (prop.type) {
        case 'string':
          defaults[prop.name] = String(prop.default_value);
          break;
        case 'number':
          defaults[prop.name] = Number(prop.default_value);
          break;
        case 'boolean':
          defaults[prop.name] = Boolean(prop.default_value);
          break;
        case 'date':
          defaults[prop.name] = new Date(prop.default_value).toISOString();
          break;
        case 'json':
          if (typeof prop.default_value === 'object') {
            defaults[prop.name] = prop.default_value;
          } else if (typeof prop.default_value === 'string') {
            try {
              defaults[prop.name] = JSON.parse(prop.default_value);
            } catch {
              defaults[prop.name] = prop.default_value;
            }
          }
          break;
        default:
          defaults[prop.name] = prop.default_value; // Catch-all for unhandled types.
      }
    }
  });

  return defaults;
};

/** The vertex label and glyph a new sphere of `schema`'s type carries. */
export const sphereIdentityFromSchema = (schema: Schema): Pick<Sphere, 'label' | 'glyph' | 'glyphColor'> => ({
  label: schema.node_label,
  glyph: schema.glyph ?? 'lucide:circle',
  glyphColor: schema.glyph_color ?? { kind: 'hex', value: '#64748B' },
});

/**
 * Initializes a new `Sphere` object with required fields based on the provided schema.
 *
 * This utility function:
 * - Sets up a base sphere with default properties derived from the schema.
 * - Merges schema-defined defaults, provided overrides, and required fields.
 * - Optionally assigns perspectives to the sphere.
 *
 * @param schema - The schema object defining the properties and their defaults.
 * @param parentSphere - The parent sphere to associate with the new sphere.
 * @param userSphere - The user sphere representing the creator.
 * @param perspectiveAssignments - Optional array of perspective assignments.
 * @param overrides - Partial sphere values to override the defaults.
 * @returns A fully initialized Sphere object.
 */
export const initializeSphere = (
  schema: Schema,
  parentSphere: Sphere,
  userSphere?: Sphere,
  perspectiveAssignments?: PerspectiveAssignments,
  overrides: Partial<Sphere> = {},
  organizationId?: string,
  /**
   * When true, the sphere is deliberately created without a Purpose parent:
   * the auto-assign-from-parent-sphere default below is skipped and
   * `purpose_unplaced` is written true. Throws {@link UnplacedWithParentError}
   * if the caller also supplied an explicit `center_parent` assignment.
   */
  unplaced?: boolean
): Sphere => {
  const baseSphere: Sphere = {
    id: '', // Default ID, expected to be generated later.
    ...sphereIdentityFromSchema(schema),
    properties: {
      sphereId: organizationId || userSphere?.properties['sphereId'], // Associate the sphere with the given organization.
      type: schema.system_name, // Type of sphere as defined in the schema.
      name: '', // Default name (to be populated later).
      description: '', // Default empty description.
      // NOTE: `urgency` is deliberately NOT seeded here. It is an edge-defined
      // property (`defined_on: 'edge'`, schema default_value: 3 = Medium) written
      // onto the perspective edge, not the vertex. Hard-coding a value here would
      // always win over the schema default — `extractDefaults()` skips edge-defined
      // props, so the schema's default never merges in — which silently forced every
      // sphere created without an explicit urgency to 2 (Low) instead of 3 (Medium).
      // Leaving it absent lets createSphere's edge-property pass
      // (filterAndValidateProperties(..., assignDefaults: true)) apply the schema
      // default, while an explicit override in `overrides.properties` still wins.
    },
  };

  const schemaDefaults = extractSchemaDefaults(schema);

  // Merge properties, prioritizing base sphere, schema defaults, and user-provided overrides.
  // is_private is intentionally left undecided here — the smart default below
  // depends on the resolved center_center assignee, which is computed after
  // perspective assignments run further down.
  const initializedProperties: SphereProperties = {
    ...baseSphere.properties,
    ...schemaDefaults,
    ...(overrides.properties ?? {}),
    name: overrides.properties?.name ?? schemaDefaults.name ?? '',
  };

  const initializedSphere: Sphere = {
    ...baseSphere,
    ...overrides,
    properties: initializedProperties,
  };

  // Handle perspective assignments
  perspectiveAssignments = perspectiveAssignments ?? [];

  // Check if center_center was already provided in perspectiveAssignments (e.g., from buildPerspectiveAssignments)
  const centerCenterAlreadyAssigned = perspectiveAssignments.some(
    (assignment) => assignment.perspectiveSystemName === 'center_center'
  );
  const centerCenterAssignment = resolveCenterCenterAssignment(schema, parentSphere, centerCenterAlreadyAssigned);
  if (centerCenterAssignment) perspectiveAssignments.push(centerCenterAssignment);

  // Assign the responsible perspective to the user sphere (if not already provided).
  const responsibleAlreadyAssigned = perspectiveAssignments.some(
    (assignment) => assignment.perspectiveSystemName === 'center_responsible'
  );

  if (!responsibleAlreadyAssigned && userSphere) {
    perspectiveAssignments.push({
      perspectiveSystemName: 'center_responsible',
      assignedSphere: userSphere,
    });
  }

  // Assign the parent perspective if assignee rules are met and it's not already assigned to center_center.
  const centerParentAssignment = resolveCenterParentAssignment(
    schema,
    parentSphere,
    perspectiveAssignments,
    unplaced,
    centerCenterAlreadyAssigned
  );
  if (centerParentAssignment) perspectiveAssignments.push(centerParentAssignment);

  perspectiveAssignments = dropRedundantCenterParent(perspectiveAssignments);

  // Hard invariant: every resolved perspective assignment — explicit
  // (caller-supplied) or auto-inferred above — must satisfy its perspective's
  // assignee_filter. initializeSphere is the shared creation-time chokepoint
  // (deploy saga, calendar, identity provisioning, direct create all route
  // through it), so enforcing here rejects a structurally invalid edge (e.g. a
  // Priority parented under a Strategy) before it is ever persisted, generically
  // over whatever perspectives the schema defines. Throws
  // AssigneeFilterViolationError.
  assertPerspectiveAssignmentsValid(schema, perspectiveAssignments);

  // Resolve the urgency that WILL be written onto each perspective edge, so the
  // returned object matches what createSphere persists: an explicit override wins,
  // otherwise the schema's edge-property default (3 = Medium). Previously this was
  // hard-coded to 3 and lied whenever a caller supplied a different urgency.
  const urgencySchemaProp = schema.properties?.find((p) => p.name === 'urgency');
  const resolvedUrgency =
    (initializedProperties['urgency'] as number | undefined) ??
    (urgencySchemaProp?.default_value as number | undefined) ??
    3;

  // Populate the assigned perspectives.
  const assignedPerspectives: AssignedPerspectives = [];

  perspectiveAssignments.forEach((assignment) => {
    const perspectiveAssignment = {
      mainSphereId: initializedSphere.id,
      perspectiveProperties: {
        id: '',
        system_name: assignment.perspectiveSystemName,
        name: assignment.perspectiveSystemName,
      },
      assigned: [
        {
          sphere: assignment.assignedSphere,
          edge: { edgeId: '', urgency: resolvedUrgency },
        },
      ],
    };

    assignedPerspectives.push(perspectiveAssignment);
  });

  initializedSphere.assignedPerspectives = assignedPerspectives;

  if (unplaced) {
    initializedSphere.properties['purpose_unplaced'] = true;
  }

  // AC3: Smart default for is_private — runs AFTER perspective resolution so we
  // can inspect the resolved center_center assignee. Override and explicit schema
  // default both win.
  const privacyDefault = resolvePrivacyDefault(overrides, schemaDefaults, perspectiveAssignments, userSphere, parentSphere);
  if (privacyDefault !== undefined) {
    initializedSphere.properties['is_private'] = privacyDefault;
  }

  return initializedSphere;
};

/**
 * Determines whether two sphere references point to the same sphere.
 * Prefers id equality (when both ids are present and non-empty). Inherited-center
 * references pulled from a parent's `assignedPerspectives` can be thin objects with
 * a missing/empty id — in that case fall back to name + type identity.
 */
/**
 * Resolves the `center_center` perspective assignment: the parent sphere if it satisfies the
 * assignee filter, else — one hop up — the parent's own resolved `center_center`, if THAT
 * satisfies the filter. Returns `undefined` when `center_center` is already assigned or the
 * schema has no such perspective.
 */
function resolveCenterCenterAssignment(
  schema: Schema,
  parentSphere: Sphere,
  centerCenterAlreadyAssigned: boolean
): PerspectiveAssignment | undefined {
  const centerPerspective = schema.perspectives.find((p) => p.system_name === 'center_center');
  if (centerCenterAlreadyAssigned || !centerPerspective) return undefined;

  // Check if the parent sphere satisfies the assignee filters for "center_center"
  const filterResults = centerPerspective.assignee_filter.map((filter) => evaluateAssigneeFilter(parentSphere, filter));
  if (filterResults.some(Boolean)) {
    return { perspectiveSystemName: 'center_center', assignedSphere: parentSphere };
  }

  // Try to find parent's center_center assignment
  const parentCenterAssignment = parentSphere.assignedPerspectives?.find(
    (ap) => ap.perspectiveProperties.system_name === 'center_center'
  );
  if (!parentCenterAssignment?.assigned?.[0]?.sphere) return undefined;
  const parentCenter = parentCenterAssignment.assigned[0].sphere;

  // Verify parent's center matches our filter
  const parentCenterFilterResults = centerPerspective.assignee_filter.map((filter) => evaluateAssigneeFilter(parentCenter, filter));
  if (!parentCenterFilterResults.some(Boolean)) return undefined;

  return { perspectiveSystemName: 'center_center', assignedSphere: parentCenter as Sphere };
}

/**
 * Resolves the `center_parent` assignment when assignee rules are met and it's not already
 * assigned or subsumed by `center_center`. Throws {@link UnplacedWithParentError} if the
 * caller marked the sphere `unplaced` while also supplying a `center_parent`.
 */
function resolveCenterParentAssignment(
  schema: Schema,
  parentSphere: Sphere,
  perspectiveAssignments: PerspectiveAssignments,
  unplaced: boolean | undefined,
  centerCenterAlreadyAssigned: boolean
): PerspectiveAssignment | undefined {
  const parentPerspective = schema.perspectives.find((p) => p.system_name === 'center_parent');
  const parentAlreadyAssigned = perspectiveAssignments.some(
    (assignment) => assignment.perspectiveSystemName === 'center_parent'
  );

  if (unplaced && parentAlreadyAssigned) {
    throw new UnplacedWithParentError();
  }
  if (unplaced || parentAlreadyAssigned || !parentPerspective) return undefined;

  // assignee_filter is an OR list of alternatives — a parent passes if it
  // matches ANY filter (e.g., center_priority's center_parent allows
  // label=center OR type=center_priority OR type=center_pillar; one parent
  // can't be all three at once). Keeping this consistent with the
  // center_center evaluation above (also `.some(Boolean)`).
  const filterResults = parentPerspective.assignee_filter.map((filter) => evaluateAssigneeFilter(parentSphere, filter));
  const isAssigneeValid = parentPerspective.assignee_filter.length === 0 || filterResults.some(Boolean);

  // Check if the sphere is already assigned to center_center
  const isAssignedToCenterCenter = perspectiveAssignments.some(
    (assignment) => assignment.perspectiveSystemName === 'center_center' && assignment.assignedSphere.id === parentSphere.id
  );

  // When the caller EXPLICITLY nested this sphere inside a different container
  // (center_center provided up-front and pointing at a sphere other than the
  // creation target), do NOT default center_parent to the creation target —
  // that skips levels (the org becomes "parent" of a unit that lives inside a
  // Fleet). Mirrors the flat case, where the default is suppressed because
  // parent == center: nested children end up with center_center only, and an
  // explicit center_parent stays fully caller-controlled.
  const explicitlyNestedElsewhere =
    centerCenterAlreadyAssigned &&
    perspectiveAssignments.some(
      (assignment) =>
        assignment.perspectiveSystemName === 'center_center' &&
        assignment.assignedSphere.id !== parentSphere.id
    );

  if (isAssigneeValid && !isAssignedToCenterCenter && !explicitlyNestedElsewhere) {
    return { perspectiveSystemName: 'center_parent', assignedSphere: parentSphere };
  }
  return undefined;
}

/**
 * Invariant: a sphere must never be its OWN center and parent — i.e. the same sphere must not
 * be assigned to both `center_center` and `center_parent`. This can slip through when the
 * caller-supplied assignments (e.g. from buildPerspectiveAssignments) resolved `center_center`
 * to the parent's own inherited center, which equals the parent when creating directly under a
 * top-level center sphere. The center_center relationship already captures that, so drop the
 * redundant center_parent. (To intentionally set them equal, a caller would need to bypass
 * this helper.)
 */
function dropRedundantCenterParent(perspectiveAssignments: PerspectiveAssignments): PerspectiveAssignments {
  const resolvedCenterAssignee = perspectiveAssignments.find((a) => a.perspectiveSystemName === 'center_center')?.assignedSphere;
  if (!resolvedCenterAssignee) return perspectiveAssignments;

  const beforeCount = perspectiveAssignments.length;
  const filtered = perspectiveAssignments.filter(
    (a) => !(a.perspectiveSystemName === 'center_parent' && isSameSphere(resolvedCenterAssignee, a.assignedSphere))
  );
  if (filtered.length < beforeCount) {
    console.warn(
      '[initializeSphere] Dropped center_parent that equaled center_center — a sphere cannot be its own center and parent.',
      {
        centerId: resolvedCenterAssignee.id,
        centerName: resolvedCenterAssignee.properties?.name,
      }
    );
  }
  return filtered;
}

/**
 * AC3: Smart default for `is_private`, evaluated AFTER perspective resolution so it can inspect
 * the resolved `center_center` assignee. An explicit override or schema default both win — in
 * that case this returns `undefined` and the caller leaves `is_private` untouched.
 */
function resolvePrivacyDefault(
  overrides: Partial<Sphere>,
  schemaDefaults: Partial<SphereProperties>,
  perspectiveAssignments: PerspectiveAssignments,
  userSphere: Sphere | undefined,
  parentSphere: Sphere
): boolean | undefined {
  if ('is_private' in (overrides.properties ?? {}) || schemaDefaults['is_private']) return undefined;

  const resolvedCenter = perspectiveAssignments.find((a) => a.perspectiveSystemName === 'center_center')?.assignedSphere;
  const centerType = (resolvedCenter as any)?.properties?.type;
  const centerLabel = (resolvedCenter as any)?.label;

  // Rule 1: Sphere whose center is a user-sphere → Private ON. Anything
  // owned by a user (personal items, drafts under their subject) defaults
  // private regardless of who created it. Catches both "I created it under
  // my own subject" and "an admin created it under another user's subject".
  if (centerType === 'center_user' || centerLabel === 'subject') return true;

  // Rule 2: Sphere under user's own Subject as the IMMEDIATE parent → Private ON
  // (covers the case where parent is the creator's user-sphere even if the
  // resolved center is something else, e.g., a workspace).
  if (parentSphere?.id === userSphere?.id) return true;

  // Rule 3: Sphere under already-private parent → Private ON (inherit intent).
  // Rule 4 (otherwise → Private OFF, team work visible by default) falls out of this same check.
  return parentSphere?.properties?.['is_private'] === true || parentSphere?.properties?.['is_private'] === 'true';
}

function isSameSphere(
  a?: { id?: string; properties?: any } | null,
  b?: { id?: string; properties?: any } | null
): boolean {
  if (!a || !b) return false;
  if (a === b) return true;

  const aId = a.id ? String(a.id) : '';
  const bId = b.id ? String(b.id) : '';
  if (aId && bId) return aId === bId;

  const normType = (t: any) => (Array.isArray(t) ? t[0] : t);
  const aName = a.properties?.name;
  const bName = b.properties?.name;
  if (!aName || !bName) return false;
  return aName === bName && normType(a.properties?.type) === normType(b.properties?.type);
}

